import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card } from '../common/Card';
import { ChartBar } from '@phosphor-icons/react';

const TIME_RANGES = ['1h', '6h', '24h'];

const PLATFORMS = {
  polymarket: { name: 'Polymarket', color: '#5b8bd3' },
  draftkings: { name: 'DraftKings', color: '#2e9f7a' },
  fanduel: { name: 'FanDuel', color: '#c49a5c' },
  betmgm: { name: 'BetMGM', color: '#d36b6d' },
  caesars: { name: 'Caesars', color: '#8ea5bd' },
  betrivers: { name: 'BetRivers', color: '#7aa4a8' },
  betway: { name: 'Betway', color: '#9aa6b2' },
  ballybet: { name: 'BallyBet', color: '#b0898b' },
  pinnacle: { name: 'Pinnacle', color: '#6f8db0' },
};

export function OddsChart({ data, market, accessStatus }) {
  const { t } = useTranslation();
  const [timeRange, setTimeRange] = useState('24h');
  const [activePlatforms, setActivePlatforms] = useState(['polymarket', 'draftkings', 'fanduel', 'betmgm']);
  const [lastUpdate, setLastUpdate] = useState(new Date().toLocaleTimeString());
  const [refreshCountdown, setRefreshCountdown] = useState(30);
  const isAuthRequired = accessStatus?.isAuthRequired;
  const isMembershipRequired = accessStatus?.isMembershipRequired;

  // Countdown timer for refresh indicator
  useEffect(() => {
    const timer = setInterval(() => {
      setRefreshCountdown((prev) => (prev <= 1 ? 30 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const chartData = useMemo(() => {
    const history = Array.isArray(data?.history) ? data.history : [];
    const getPlatformValue = (item, platform) => {
      if (platform === 'polymarket') {
        return item.polymarket_yes ?? null;
      }

      const home = item[`${platform}_home`];
      const away = item[`${platform}_away`];
      if (home == null && away == null) return null;
      if (home == null) return away;
      if (away == null) return home;

      const poly = item.polymarket_yes ?? null;
      if (poly == null) return home;
      return Math.abs(home - poly) <= Math.abs(away - poly) ? home : away;
    };

    const toPercent = (value) => (value == null ? null : value * 100);

    return history.map((item) => ({
      time: item.timestamp || new Date(item.time).getTime(),
      polymarket: toPercent(getPlatformValue(item, 'polymarket')),
      draftkings: toPercent(getPlatformValue(item, 'draftkings')),
      fanduel: toPercent(getPlatformValue(item, 'fanduel')),
      betmgm: toPercent(getPlatformValue(item, 'betmgm')),
      caesars: toPercent(getPlatformValue(item, 'caesars')),
      betrivers: toPercent(getPlatformValue(item, 'betrivers')),
      betway: toPercent(getPlatformValue(item, 'betway')),
      ballybet: toPercent(getPlatformValue(item, 'ballybet')),
      pinnacle: toPercent(getPlatformValue(item, 'pinnacle')),
    }));
  }, [data]);
  const yDomain = useMemo(() => {
    const values = [];
    chartData.forEach((point) => {
      activePlatforms.forEach((platform) => {
        const val = point?.[platform];
        if (typeof val === 'number' && Number.isFinite(val)) {
          values.push(val);
        }
      });
    });

    if (values.length === 0) {
      return [0, 100];
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = Math.max(2, (max - min) * 0.1);
    return [Math.max(0, min - padding), Math.min(100, max + padding)];
  }, [chartData, activePlatforms]);
  const emptyState = isAuthRequired
    ? { text: t('auth.loginRequired', '请先登录'), className: 'text-accent' }
    : isMembershipRequired
    ? { text: t('membership.noAccess', '没有会员权限'), className: 'text-accent-warm' }
    : { text: t('detail.noChartData'), className: 'text-gray-500' };

  // Calculate stats
  const polymarketData = chartData.map(d => d.polymarket).filter((value) => typeof value === 'number');
  const stats = {
    high: polymarketData.length > 0 ? Math.max(...polymarketData).toFixed(1) : '-',
    low: polymarketData.length > 0 ? Math.min(...polymarketData).toFixed(1) : '-',
    volatility: polymarketData.length > 1 
      ? (Math.max(...polymarketData) - Math.min(...polymarketData)).toFixed(1)
      : '-',
    dataPoints: data?.dataPoints || chartData.length,
  };

  // Calculate change
  const change = polymarketData.length > 1 
    ? (polymarketData[polymarketData.length - 1] - polymarketData[0]).toFixed(1)
    : 0;

  const togglePlatform = (platform) => {
    setActivePlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };

  return (
    <Card>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-accent flex items-center gap-2">
          <ChartBar size={20} weight="duotone" /> {t('detail.oddsHistory')}
        </h3>
        <div className="flex items-center gap-3">
          {/* Refresh indicator */}
          <span className="text-xs text-gray-400">
            {refreshCountdown}s {t('detail.refresh')}
          </span>
          
          {/* Time range buttons */}
          <div className="flex gap-1">
            {TIME_RANGES.map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-2 py-1 text-xs rounded ${
                  timeRange === range
                    ? 'bg-accent-strong text-white'
                    : 'bg-surface-highlight/70 text-gray-400 hover:bg-surface-highlight'
                }`}
              >
                {range}
              </button>
            ))}
          </div>

          {/* Change indicator */}
          <span className={`text-sm font-medium ${parseFloat(change) >= 0 ? 'text-positive' : 'text-negative'}`}>
            {parseFloat(change) >= 0 ? '+' : ''}{change}%
          </span>

          {/* Last update */}
          <span className="text-xs text-gray-400">{lastUpdate}</span>
          <button 
            className="text-gray-400 hover:text-white transition-colors"
            onClick={() => setLastUpdate(new Date().toLocaleTimeString())}
          >
            ⟳
          </button>
        </div>
      </div>

      {/* Platform toggles */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {Object.entries(PLATFORMS).map(([key, { name, color }]) => (
          <button
            key={key}
            onClick={() => togglePlatform(key)}
            className={`px-2 py-1 text-xs rounded transition-all ${
              activePlatforms.includes(key)
                ? 'opacity-100'
                : 'opacity-40'
            }`}
            style={{ 
              backgroundColor: activePlatforms.includes(key) ? `${color}20` : 'transparent',
              color: color,
              border: `1px solid ${activePlatforms.includes(key) ? color : '#4b5563'}`
            }}
          >
            {name}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div className="h-64">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <XAxis
                dataKey="time"
                tick={{ fill: '#9aa6b2', fontSize: 10 }}
                tickFormatter={(v) => {
                  const date = new Date(v);
                  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                }}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={yDomain}
                tick={{ fill: '#9aa6b2', fontSize: 10 }}
                tickFormatter={(v) => `${v}%`}
                width={40}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#141e28', border: '1px solid rgba(148, 163, 184, 0.12)', borderRadius: 8 }}
                labelFormatter={(v) => new Date(v).toLocaleString()}
                formatter={(value) => value ? [`${value.toFixed(1)}%`] : ['-']}
              />
              <Legend 
                wrapperStyle={{ paddingTop: 10 }}
                formatter={(value) => <span style={{ color: '#9aa6b2', fontSize: 12 }}>{value}</span>}
              />
              {activePlatforms.map((platform) => (
                <Line
                  key={platform}
                  type="monotone"
                  dataKey={platform}
                  stroke={PLATFORMS[platform]?.color || '#6f8db0'}
                  strokeWidth={2.4}
                  strokeOpacity={0.95}
                  dot={false}
                  name={PLATFORMS[platform]?.name || platform}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className={`h-full flex items-center justify-center ${emptyState.className}`}>
            {emptyState.text}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mt-4 pt-4 border-t border-glass-border">
        <div className="text-center">
          <div className="text-xs text-gray-400">{t('detail.highest')}</div>
          <div className="font-medium text-positive">{stats.high}%</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-400">{t('detail.lowest')}</div>
          <div className="font-medium text-negative">{stats.low}%</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-400">{t('detail.volatility')}</div>
          <div className="font-medium text-accent-warm">+{stats.volatility}%</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-400">{t('detail.dataPoints')}</div>
          <div className="font-medium">{stats.dataPoints}</div>
        </div>
      </div>
    </Card>
  );
}
